import { CardView } from "../CardView/CardView"
import "./TableView.css"
import { Row, Col } from "../BSReplace"
import { RefreshButton, type RefreshButtonProps } from "../shadcn/components/atoms/buttons/RefreshButton"
import type { MouseEventHandler, MouseEvent } from "react"
import { useCallback, useState, memo, useMemo } from "react"
import arrow from "../../images/collapse-arrow.png"
import { useSelectionContext, SelectorInput } from "../SelectionContext/SelectionContext"
import { Tooltip, TooltipTrigger, TooltipContent } from "../shadcn/components/ui/tooltip"
import noInfoImage from "shared/src/images/noInfoImage.svg"
import type { TableSizes } from "./useTableSizes"
import { useTableSizes } from "./useTableSizes"
import useOverflowDetection from "./useOverflowDetection"

const rowPropsAreEqual = (prevProps: TableRowProps, nextProps: TableRowProps) => {
	const { info: prevInfo, ...otherPrevProps } = prevProps
	const { info: nextInfo, ...otherNextProps } = nextProps
	for (const infoKey of Object.keys(nextInfo)) {
		if (nextInfo[infoKey] !== prevInfo[infoKey]) {
			return false
		}
	}
	for (const key of Object.keys(otherNextProps)) {
		if (otherNextProps[key] !== otherPrevProps[key]) {
			return false
		}
	}
	return true
}

interface TableRowProps {
	info: any
	useSelection?: boolean
	infoKeys: string[]
	rowId: string
	rowFunc: (rowId: string) => void
	preprocessing?: ((a: any) => any)[]
	handleOnClick?: (e: any) => void
	className?: string
	highlighted?: boolean
	tableSizes?: TableSizes
	preventSelector?: boolean
}

const TableRow = memo<TableRowProps>(
	({
		info,
		useSelection,
		infoKeys,
		rowId,
		rowFunc,
		preprocessing,
		handleOnClick,
		highlighted,
		tableSizes,
		preventSelector,
		...props
	}) => {
		return (
			<Row
				{...props}
				onClick={() => {
					rowFunc?.(rowId)
				}}
				className={`tableRow flex-nowrap ${rowFunc == null ? "" : "clickable"} standardPadding ${props.className} ${
					highlighted ? "highlighted-table-row" : ""
				}`}>
				{/* <div className="selection-col"> */}
				{preventSelector ? <div className="selection"></div> : <RowSelector rowId={rowId} showSelectedCount={false} />}
				{/* </div> */}
				{infoKeys.map((infoKey, index) => (
					<TableCol key={rowId + infoKey} tableSizes={tableSizes} {...{ info, infoKey, rowId, index, preprocessing }} />
				))}
			</Row>
		)
	},
	rowPropsAreEqual
)

const MAX_PER_PAGE = 30

export interface SortableTableProps {
	sortFunc?: SortFunc
	headings: string[]
	info: any[]
	infoKeys: string[]
	highlighted?: string[]
	rowIdKey: string
	rowFunc?: (rowId: string) => void
	preprocessing?: ((a: any, row: string) => any)[]
	individualSortFuncs?: SortFunc[]
	noInfoData?: { title: string; description?: string; children?: React.ReactNode }
	pageNumber?: number
	setPageNumber?: (newNumber: number) => void
	summaryRow?: Array<(info) => React.ReactNode>
	tableSizes?: TableSizes
}

interface SearchProps {
	searchTermRef: React.MutableRefObject<string>
	setSearchTerm: React.Dispatch<React.SetStateAction<string>>
}

interface SortableTableCardProps extends SortableTableProps {
	title?: string
	refreshProps?: RefreshButtonProps
	children?: React.ReactNode
	search?: SearchProps
	primaryActions?: React.ReactNode
}

interface CurrentSortData {
	index: number
	direction: 1 | -1
}

export type SortFunc<T = any> = (a: T, b: T) => number

function useTableSorting(defaultSortFunc: SortFunc | null, individualSortFuncs: SortFunc[]) {
	const [currentSortData, setCurrentSortData] = useState<CurrentSortData>(null)
	const [currentSortFunc, setCurrentSortFunc] = useState<SortFunc | null>(() => defaultSortFunc)

	const sortFunctions: Array<[MouseEventHandler<HTMLElement> | null, MouseEventHandler<HTMLElement> | null]> = useMemo(
		() =>
			individualSortFuncs?.map((sortFunc, index) => {
				const setFunc: MouseEventHandler<HTMLElement> = sortFunc
					? (e: MouseEvent<HTMLElement>) => {
							if (currentSortData?.index === index && currentSortData?.direction === 1) {
								console.log("set back to default")
								setCurrentSortData({ index: null, direction: null })
								setCurrentSortFunc(() => defaultSortFunc)
							} else {
								setCurrentSortData({ index: index, direction: 1 })
								setCurrentSortFunc(() => sortFunc)
							}
						}
					: null
				const setReversedFunc = sortFunc
					? (e: MouseEvent<HTMLElement>) => {
							if (currentSortData?.index === index && currentSortData?.direction === -1) {
								console.log("set back to default")
								setCurrentSortData({ index: null, direction: null })
								setCurrentSortFunc(() => defaultSortFunc)
							} else {
								setCurrentSortData({ index: index, direction: -1 })
								setCurrentSortFunc(() => (a, b) => -sortFunc(a, b))
							}
						}
					: null
				return [setFunc, setReversedFunc]
			}) ?? [],
		[currentSortData?.direction, currentSortData?.index, defaultSortFunc, individualSortFuncs]
	)

	return { currentSortFunc, sortFunctions, currentSortData }
}

export function SortableTableCard({ title = null, refreshProps, children, ...props }: SortableTableCardProps) {
	return (
		<CardView className="tableView">
			{refreshProps !== undefined && <RefreshButton {...refreshProps} />}
			{title ? <h4 className="table-title bs-h4">{title}</h4> : <></>}
			<SortableTable {...props}></SortableTable>
			{children}
		</CardView>
	)
}

export function SortableTable({
	sortFunc,
	headings,
	info,
	infoKeys,
	rowIdKey,
	rowFunc,
	preprocessing,
	highlighted,
	individualSortFuncs,
	noInfoData = { title: "Nothing to show" },
	pageNumber = 0,
	setPageNumber = null,
	summaryRow = null,
	tableSizes,
}: Readonly<SortableTableProps>) {
	const { currentSortFunc, currentSortData, sortFunctions } = useTableSorting(sortFunc, individualSortFuncs)
	info = useMemo(() => (currentSortFunc != null ? info.sort(currentSortFunc) : info), [currentSortFunc, info])
	const [pageNumberInternal, setPageNumberInternalBase] = useState(pageNumber)

	useMemo(() => {
		setPageNumberInternalBase(pageNumber)
	}, [pageNumber])

	const setPageNumberInternal = useCallback(
		(newNumber) => {
			setPageNumberInternalBase(newNumber)
			setPageNumber?.(newNumber)
		},
		[setPageNumber]
	)
	return (
		<Row className="tableScrollContent" role="table">
			<Col className="tableContent">
				<TitleRow
					headings={headings}
					currentSortData={currentSortData}
					sortFunctions={sortFunctions}
					tableSizes={tableSizes}
				/>

				{info.length > 0 ? (
					<>
						{summaryRow && (
							<SummaryRow
								summaryRow={summaryRow}
								info={info}
								tableSizes={tableSizes}
								rowProps={{ className: "summary-row" }}
								noCols={infoKeys?.length}
								preprocessing={preprocessing}
							/>
						)}
						{info
							.slice(pageNumberInternal * MAX_PER_PAGE, (pageNumberInternal + 1) * MAX_PER_PAGE)
							.map((rowInfo, index) => {
								const {
									rowProps,
									[rowIdKey]: rowId = "empty_row_id" + Math.random().toString(36).substring(7),
									...otherInfo
								} = rowInfo
								return (
									<TableRow
										role="row"
										key={rowId}
										{...(rowProps ?? {})}
										info={otherInfo}
										infoKeys={infoKeys}
										preprocessing={preprocessing}
										rowId={rowId}
										rowFunc={rowFunc}
										tableSizes={tableSizes}
										highlighted={highlighted?.includes(rowId)}
									/>
								)
							})}
						<Pager pageNumber={pageNumberInternal} setPageNumber={setPageNumberInternal} total={info.length} />
					</>
				) : (
					<div className="noInfoPlaceholder">
						<div className="noInfoPlaceholderContent">
							<img src={noInfoImage} alt="No data found" />
							<div className="noInfoPlaceholderText">
								<p>{noInfoData.title}</p>
								{noInfoData.description && <p>{noInfoData.description}</p>}
							</div>
							{noInfoData.children}
						</div>
					</div>
				)}
			</Col>
		</Row>
	)
}

function SummaryRow({ summaryRow, tableSizes, info, rowProps, noCols, preprocessing }) {
	const summaryInfo = summaryRow.map((rowFunc) => rowFunc?.(info))
	const infoKeys = new Array(noCols).fill(0).map((_, i) => i)

	return (
		<TableRow
			role="row"
			{...(rowProps ?? {})}
			info={summaryInfo}
			infoKeys={infoKeys}
			rowId={"summary_row"}
			tableSizes={tableSizes}
			preprocessing={preprocessing}
			preventSelector={true}
		/>
	)
}

function Pager({
	pageNumber,
	setPageNumber,
	total,
}: {
	pageNumber: number
	setPageNumber: (newNumber: number) => void
	total: number
}) {
	return (
		total > MAX_PER_PAGE && (
			<div className="pager">
				<div className="pager-back">
					{pageNumber > 0 && (
						<img
							className="clickable"
							onClick={() => {
								setPageNumber(pageNumber - 1)
							}}
							src={arrow}
							alt="back"></img>
					)}
				</div>
				<div className="pager-number">{`${pageNumber * MAX_PER_PAGE + 1} - ${Math.min(
					(pageNumber + 1) * MAX_PER_PAGE,
					total
				)}`}</div>
				<div className="pager-forward">
					{(pageNumber + 1) * MAX_PER_PAGE < total && (
						<img
							className="clickable"
							onClick={() => {
								setPageNumber(pageNumber + 1)
							}}
							src={arrow}
							alt="forward"></img>
					)}
				</div>
			</div>
		)
	)
}

// function tryOptions(info, infoKey) {
//     //If infoKey is an Array sohuld try each in order before leaving blank
//     if (Array.isArray(infoKey)) {
//         for (var i = 0; i < infoKey.length; i++) {
//             if (info[infoKey[i]] !== undefined) {
//                 return info[infoKey[i]]
//             } else {
//                 continue
//             }
//         }
//     }
//     else {
//         return info[infoKey]
//     }

// }

function TitleRow({
	headings,
	currentSortData,
	sortFunctions,
	tableSizes,
}: {
	headings: string[]
	currentSortData: CurrentSortData
	sortFunctions: Array<[MouseEventHandler<HTMLElement> | null, MouseEventHandler<HTMLElement> | null]>
	tableSizes?: TableSizes
}) {
	const sizes = useTableSizes(tableSizes)

	return (
		<Row className="titleRow flex-nowrap standardPadding" data-testid="title-row">
			{<RowSelector rowId={"all"} showSelectedCount={true} />}
			{headings?.map((heading, index) => (
				<Col
					style={{ display: "flex", alignItems: "center", gap: "6px", flex: "1 1 0", minWidth: 0 }}
					className="titleRowCol"
					xl={sizes?.xl[index]}
					lg={sizes?.lg[index]}
					md={sizes?.md[index]}
					sm={sizes?.sm[index]}
					xs={sizes?.xs[index]}
					key={heading}>
					<div className="tableColContent titleRowColContent">{heading}</div>
					{(sortFunctions?.[index]?.[0] || sortFunctions?.[index]?.[1]) && (
						<div>
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										data-testid={`sort-asc-${heading.replace(/\s+/g, "-")}`}
										className="icon-up-arrow clickable"
										style={{
											width: 5.5,
											margin: "5px 3px",
											borderTopColor:
												currentSortData?.index === index && currentSortData?.direction === -1 ? "black" : "#aaaaaa",
										}}
										onClick={sortFunctions[index][1]}></div>
								</TooltipTrigger>
								<TooltipContent>Sort {heading} Ascending</TooltipContent>
							</Tooltip>
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										data-testid={`sort-desc-${heading.replace(/\s+/g, "-")}`}
										className="icon-down-arrow clickable"
										style={{
											width: 5.5,
											margin: "5px 3px",
											borderBottomColor:
												currentSortData?.index === index && currentSortData?.direction === 1 ? "black" : "#aaaaaa",
										}}
										onClick={sortFunctions[index][0]}></div>
								</TooltipTrigger>
								<TooltipContent>Sort {heading} Descending</TooltipContent>
							</Tooltip>
						</div>
					)}
				</Col>
			))}
		</Row>
	)
}

interface TableColProps {
	info: any
	infoKey: string
	rowId: string
	tableSizes?: TableSizes
	index: number
	preprocessing?: ((a: any) => any)[]
}

function TableCol({ info, infoKey, rowId, index, preprocessing, tableSizes, ...props }: TableColProps) {
	const preprocess = (preprocessing ? preprocessing[index] : null) || ((a, b) => a)
	const data = info[infoKey]
	const sizes = useTableSizes(tableSizes)

	const colText = useMemo(() => (data !== undefined ? preprocess(data, rowId) : "-"), [data, preprocess, rowId])
	const { contentRef, isOverflowing } = useOverflowDetection()
	const disableTooltip = typeof colText !== "string"

	return (
		<Col
			className="tableCol"
			data-testid={`column-${infoKey}`}
			xs={sizes?.xs[index]}
			sm={sizes?.sm[index]}
			md={sizes?.md[index]}
			lg={sizes?.lg[index]}
			xl={sizes?.xl[index]}
			{...props}>
			<div style={{ width: "100%", minWidth: 0 }}>
				{isOverflowing && !disableTooltip ? (
					<Tooltip>
						<TooltipTrigger asChild>
							<div ref={contentRef} className="tableColContent">
								{colText}
							</div>
						</TooltipTrigger>
						<TooltipContent side="top">{colText}</TooltipContent>
					</Tooltip>
				) : (
					<div ref={contentRef} className="tableColContent">
						{colText}
					</div>
				)}
			</div>
		</Col>
	)
}

function RowSelector({ rowId, showSelectedCount = false }: { rowId: string; showSelectedCount?: boolean }) {
	const {
		currentSelection: { [rowId]: isSelected },
		changeSelectionForId,
		selectedCount,
	} = useSelectionContext()

	return (
		selectedCount != null && (
			<div
				className="selection"
				onClick={(e) => {
					e.stopPropagation()
				}}>
				<SelectorInput rowId={rowId} isSelected={isSelected} changeSelectionForId={changeSelectionForId} />
				{showSelectedCount === true && (
					<div className="selection-count">{selectedCount > 0 ? `${selectedCount} selected` : " "}</div>
				)}
			</div>
		)
	)
}
